import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";
import { faLayerGroup, faListUl, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppShell, { PageHeader } from "../components/shell/app-shell";
import Icon3D from "../components/ui/icon-3d";
import SearchInput from "../components/ui/search-input";
import Select from "../components/ui/select";
import Segmented from "../components/ui/segmented";
import SidePanel from "../components/ui/side-panel";
import StatusPill from "../components/ui/status-pill";
import PulseDot from "../components/ui/pulse-dot";
import EmptyState from "../components/ui/empty-state";
import SkeletonList from "../components/ui/skeleton-list";
import { LatencyBadge } from "../components/ui/latency";
import LogDetail from "../components/logs/log-detail";
import GroupPanel from "../components/logs/group-panel";
import TimeAgo from "../components/time-ago";
import ListFooter from "../components/pagination/list-footer";
import PolicyChip from "../components/logs/policy-chip";
import useQueryState, { useDebouncedQueryState, usePageParam } from "../hooks/use-query-state";
import useAutoRefresh from "../hooks/use-auto-refresh";
import { usePlatform, platformParam } from "../store/use-platform";
import { ApiLog, ApiLogDetail, LogGroup, PAYMENT_DECLINE_TYPE, fetchLog, fetchLogGroups, fetchLogs, policyOf } from "../utils/api";
import { CATEGORY_LOOK, HEALTH_LOOK, PLATFORM_LOOK, platformLabel, rangeStart } from "../utils/status";
import { ParseErrors } from "../utils/ParseErrors";

const PAGE_SIZE = 30;

const OUTCOMES = [
  { label: "Errors", value: "errors" },
  { label: "Critical (5xx)", value: "critical" },
  { label: "Warnings (4xx)", value: "unhealthy" },
  { label: "Success", value: "healthy" },
  { label: "Everything", value: "all" },
];
const OUTCOME_STATUS: Record<string, string | undefined> = { errors: "unhealthy,critical", critical: "critical", unhealthy: "unhealthy", healthy: "healthy", all: undefined };

const STATES = [{ label: "Open", value: "open" }, { label: "Muted", value: "muted" }, { label: "Resolved", value: "resolved" }, { label: "Any", value: "all" }];
const CATEGORIES = [{ label: "All", value: "" }, ...Object.entries(CATEGORY_LOOK).map(([value, look]) => ({ label: look.label, value }))];
const ENVIRONMENTS = [{ label: "Production", value: "production" }, { label: "UAT", value: "uat" }, { label: "Local", value: "local" }];
const RANGES = [{ label: "Last hour", value: "1h" }, { label: "Last 24 hours", value: "24h" }, { label: "Last 7 days", value: "7d" }, { label: "Last 30 days", value: "30d" }, { label: "Any time", value: "all" }];

const METHOD_STYLE: Record<string, string> = {
  GET: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
  POST: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
  PUT: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
  PATCH: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
  DELETE: "bg-rose-500/10 text-rose-600 dark:text-rose-300",
};

// ----------------------- SPLIT "route:POST /x" INTO METHOD + PATH ------------------------------------
const splitLink = (link: string) => {
  const match = link.replace(/^route:/, "").match(/^([A-Z]+) (.+)$/);
  return match ? { method: match[1], path: match[2] } : { method: null, path: link.replace(/^\w+:/, "") };
};

function RouteLabel({ link, status, live }: { link: string; status: ApiLog["status"]; live?: boolean }) {
  const route = splitLink(link);
  return (
    <span className="flex min-w-0 items-center gap-2">
      <PulseDot tone={HEALTH_LOOK[status].tone} live={live} />
      {route.method ? <span className={clsx("rounded-md px-1.5 py-0.5 font-mono text-[10px] font-bold", METHOD_STYLE[route.method] ?? "bg-overlay-mid")}>{route.method}</span> : null}
      <span className="truncate font-mono text-[12.5px] text-heading-color">{route.path}</span>
    </span>
  );
}

function PlatformLabel({ platform }: { platform: ApiLog["platform"] }) {
  const look = PLATFORM_LOOK[platform];
  return (
    <span className="flex items-center gap-2 font-[Gilroy-Medium] text-[13px] text-heading-color">
      {look ? <Icon3D icon={look.icon} tone={look.tone} size="sm" flat className="!h-6 !w-6 !rounded-lg !text-[10px]" /> : null}
      {platformLabel(platform)}
    </span>
  );
}

// ----------------------- ONE REQUEST'S PANEL (ALL REQUESTS VIEW) ------------------------------------
function LogPanel({ id, onClose }: { id: number | null; onClose: () => void }) {
  const [log, setLog] = useState<ApiLogDetail | null>(null);

  useEffect(() => {
    setLog(null);
    if (!id) return;
    fetchLog(id).then(setLog).catch((error) => toast.error(ParseErrors(error)));
  }, [id]);

  const look = log?.routeCategory ? CATEGORY_LOOK[log.routeCategory] : CATEGORY_LOOK.general;
  const route = log ? splitLink(log.link) : null;

  return (
    <SidePanel
      open={!!id}
      onClose={onClose}
      icon={<Icon3D icon={look.icon} tone={look.tone} size="lg" />}
      title={route ? <span className="font-mono text-[15px]">{route.method ? `${route.method} ` : ""}{route.path}</span> : "Loading…"}
      subtitle={log ? `${platformLabel(log.platform)} · ${look.label} · ${log.environment ?? "production"}` : undefined}
      badge={
        log ? (
          <>
            <StatusPill tone={HEALTH_LOOK[log.status].tone} live={log.status === "critical"}>{log.statusCode ?? "—"} · {HEALTH_LOOK[log.status].label}</StatusPill>
            {log.count > 1 ? <StatusPill tone="indigo">×{log.count} in one minute</StatusPill> : null}
          </>
        ) : null
      }
    >
      {log ? <LogDetail log={log} /> : <SkeletonList rows={4} height="h-20" />}
    </SidePanel>
  );
}

export default function LogsPage() {
  const { platform } = usePlatform();
  const [view, setView] = useQueryState<string>("view", "incidents");
  const [kind, setKind] = useQueryState<string>("kind", "errors");
  const [state, setState] = useQueryState<string>("state", "open");
  const [search, searchInput, setSearchInput] = useDebouncedQueryState("search");
  const [policy, policyInput, setPolicyInput] = useDebouncedQueryState("policy");
  const [outcome, setOutcome] = useQueryState<string>("outcome", "errors");
  const [category, setCategory] = useQueryState<string>("category", "");
  const [environment, setEnvironment] = useQueryState<string>("environment", "production");
  const [range, setRange] = useQueryState<string>("range", view === "incidents" ? "7d" : "24h");
  const [page, setPage] = usePageParam();
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [groups, setGroups] = useState<LogGroup[]>([]);
  const [kinds, setKinds] = useState({ errors: 0, declines: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<LogGroup | null>(null);
  const incidents = view === "incidents";

  const load = useCallback(async () => {
    const shared = { search, platform: platformParam(platform), environment, routeCategory: category, startDate: rangeStart(range), page, limit: PAGE_SIZE };
    try {
      if (incidents) {
        const result = await fetchLogGroups({ ...shared, kind, view: state });
        setGroups(result.data);
        setKinds(result.kinds);
        setTotal(result.totalCount);
      } else {
        const result = await fetchLogs({ ...shared, status: OUTCOME_STATUS[outcome], policyRef: policy.trim() });
        setLogs(result.data);
        setTotal(result.totalCount);
      }
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setLoading(false);
    }
  }, [incidents, search, policy, platform, environment, category, range, page, kind, state, outcome]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);
  useAutoRefresh(load, 15000, live);

  return (
    <AppShell>
      <PageHeader
        title="API logs"
        subtitle={incidents ? "Repeated requests merged into one incident per platform. Open one to see every occurrence, or mute what you expect." : "Every failing request and provider error, one row per log."}
        actions={
          <button
            type="button"
            onClick={() => setLive(!live)}
            className={clsx("flex h-10 items-center gap-2 rounded-xl border px-3.5 font-[Gilroy-Medium] text-[13px] transition-colors", live ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" : "border-edge bg-surface text-light-gray")}
          >
            {live ? <PulseDot tone="emerald" live /> : <FontAwesomeIcon icon={faRotateRight} />}
            {live ? "Live · every 15s" : "Paused"}
          </button>
        }
      />

      <div className="relative z-20 mb-3 flex flex-wrap items-center gap-3">
        <Segmented label="View" value={view} onChange={setView} options={[{ label: "Incidents", value: "incidents" }, { label: "All requests", value: "requests" }]} />
        {incidents ? (
          <Segmented
            label="Kind"
            value={kind}
            onChange={setKind}
            options={[
              { label: "Errors", value: "errors", count: kinds.errors },
              { label: "Payment declines", value: "declines", count: kinds.declines },
              { label: "All", value: "all" },
            ]}
          />
        ) : null}
      </div>

      <div className="glass relative z-20 mb-4 flex flex-col gap-2 p-2.5 lg:flex-row lg:flex-wrap lg:items-center">
        <SearchInput value={searchInput} onChange={setSearchInput} placeholder={incidents ? "Search route or message" : "Search route, message, policy ref or request id"} />
        {incidents ? null : <SearchInput value={policyInput} onChange={setPolicyInput} placeholder="Policy ref" />}
        {incidents ? <Select label="State" value={state} options={STATES} onChange={setState} /> : <Select label="Show" value={outcome} options={OUTCOMES} onChange={setOutcome} />}
        <Select label="Category" value={category} options={CATEGORIES} onChange={setCategory} />
        <Select label="Env" value={environment} options={ENVIRONMENTS} onChange={setEnvironment} />
        <Select label={incidents ? "Seen" : "Time"} value={range} options={RANGES} onChange={setRange} />
      </div>

      <div className="glass overflow-hidden p-0">
        {incidents ? (
          <div className="hidden grid-cols-[90px_150px_1fr_80px_120px_70px] gap-3 border-b border-edge px-5 py-3 font-[Gilroy-Medium] text-[11px] uppercase tracking-wider text-label-muted md:grid">
            <span>Count</span><span>Platform</span><span>Incident</span><span>Status</span><span>Last seen</span><span>State</span>
          </div>
        ) : (
          <div className="hidden grid-cols-[110px_150px_1fr_80px_110px_110px] gap-3 border-b border-edge px-5 py-3 font-[Gilroy-Medium] text-[11px] uppercase tracking-wider text-label-muted md:grid">
            <span>When</span><span>Platform</span><span>Request</span><span>Status</span><span>Response</span><span>Category</span>
          </div>
        )}

        {loading ? (
          <div className="p-4"><SkeletonList /></div>
        ) : incidents && !groups.length ? (
          <EmptyState icon={faLayerGroup} tone="emerald" title={kind === "declines" ? "No payment declines" : "No incidents here"} text="Nothing matches these filters. Try another state, a wider time range or another platform." />
        ) : !incidents && !logs.length ? (
          <EmptyState icon={faListUl} tone="emerald" title="No API problems here" text="Nothing matches these filters. Try a wider time range or another platform." />
        ) : incidents ? (
          <ul className="stagger">
            {groups.map((group) => {
              const decline = group.type === PAYMENT_DECLINE_TYPE;
              return (
                <li key={group.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedGroup(group)}
                    className={clsx(
                      "grid w-full grid-cols-1 gap-2 border-b border-edge px-5 py-3.5 text-left transition-colors last:border-0 hover:bg-overlay md:grid-cols-[90px_150px_1fr_80px_120px_70px] md:items-center md:gap-3",
                      selectedGroup?.id === group.id && "bg-active-background",
                      group.state === "muted" && "opacity-60",
                    )}
                  >
                    <span className="font-[Gilroy-Bold] text-[18px] tabular-nums text-heading-color">{group.count.toLocaleString()}<span className="ml-0.5 text-[12px] text-label-muted">×</span></span>
                    <PlatformLabel platform={group.platform} />
                    <span className="min-w-0">
                      <RouteLabel link={group.route} status={decline ? "unhealthy" : group.status} live={!decline && group.state === "open" && group.status === "critical"} />
                      <span className="mt-0.5 flex items-center gap-2 font-[Gilroy-Medium] text-[12px] text-light-gray">
                        {decline ? <span className="shrink-0 rounded-md bg-amber-500/10 px-1.5 text-amber-700 dark:text-amber-300">Payment decline</span> : null}
                        <span className="truncate">{group.message}</span>
                      </span>
                    </span>
                    <span className={clsx("font-mono text-[13px] font-semibold", !decline && group.status === "critical" ? "text-rose-600 dark:text-rose-300" : "text-amber-600 dark:text-amber-300")}>{group.statusCode}</span>
                    <span className="font-[Gilroy-Medium] text-[12px] text-light-gray"><TimeAgo value={group.lastSeen} /></span>
                    <span className="font-[Gilroy-Medium] text-[12px] capitalize text-label-muted">{group.state === "acknowledged" ? "ack'd" : group.state}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <ul className="stagger">
            {logs.map((log) => (
              <li key={log.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(log.id)}
                  className={clsx(
                    "grid w-full grid-cols-1 gap-2 border-b border-edge px-5 py-3.5 text-left transition-colors last:border-0 hover:bg-overlay md:grid-cols-[110px_150px_1fr_80px_110px_110px] md:items-center md:gap-3",
                    selectedId === log.id && "bg-active-background",
                  )}
                >
                  <span className="font-[Gilroy-Medium] text-[12px] text-light-gray"><TimeAgo value={log.createdAt} /></span>
                  <PlatformLabel platform={log.platform} />
                  <span className="flex min-w-0 items-center gap-2">
                    <RouteLabel link={log.link} status={log.status} live={log.status === "critical"} />
                    <PolicyChip value={policyOf(log)} />
                    {log.count > 1 ? <span className="shrink-0 rounded-full bg-overlay-mid px-1.5 font-[Gilroy-Bold] text-[11px] text-light-gray">×{log.count}</span> : null}
                  </span>
                  <span className={clsx("font-mono text-[13px] font-semibold", log.status === "critical" ? "text-rose-600 dark:text-rose-300" : "text-amber-600 dark:text-amber-300")}>{log.statusCode ?? "—"}</span>
                  <span><LatencyBadge ms={log.responseTime} /></span>
                  <span className="font-[Gilroy-Medium] text-[12px] text-light-gray">{log.type === PAYMENT_DECLINE_TYPE ? "Payment decline" : log.routeCategory ? CATEGORY_LOOK[log.routeCategory].label : "—"}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ListFooter page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
      <LogPanel id={selectedId} onClose={() => setSelectedId(null)} />
      <GroupPanel group={selectedGroup} onClose={() => setSelectedGroup(null)} onChanged={load} />
    </AppShell>
  );
}
